import { DatePipe } from '@angular/common';
import moment, { Moment } from 'moment';
import currentWeekNumber from 'current-week-number';
import { DurationCharacterLength } from '../constants/duration-character-lengths';
import { TranslateService } from '@ngx-translate/core';

export default class DateTimeHelper {
    static dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

    static months = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

    static calendarMonths = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

    static addTimeToDate(date: Date | null, time: string | null): Date | null {
        if (date) {
            let newDate: Date = new Date(date);

            if (time) {
                const parts = time.split(':');
                const hours = Number(parts[0]);
                const minutes = Number(parts[1]);

                newDate.setHours(hours, minutes, 0, 0);
            }

            return newDate;
        }

        return null;
    }

    static getTimeFromDate(datePipe: DatePipe, date: Date | null): string | null {
        return datePipe.transform(date, 'HH:mm');
    }

    static getDateOnly(date: Date): Date {
        return new Date(date.toDateString());
    }

    static getlocalDateOnlyFromUTC(date: Date): Date {
        const result = new Date(date);
        result.setHours(0);
        result.setMinutes(0);

        return result;
    }

    static addMonths(date: Date, months: number): Date {
        let result = new Date(date);
        result.setMonth(date.getMonth() + months);

        return result;
    }

    static addHours(date: Date, hours: number): Date {
        let result = new Date(date);
        result.setHours(date.getHours() + hours);

        return result;
    }

    static addMinutes(date: Date, minutes: number): Date {
        let result = new Date(date);
        result.setMinutes(date.getMinutes() + minutes);

        return result;
    }

    static getFirstDayOfMonthDate(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), 1);
    }

    static getLastDayOfMonthDate(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0);
    }

    static getFirstDayOfYear(date: Date): Date {
        return new Date(date.getFullYear(), 0, 1, 0, 0, 0);
    }

    static getLastDayOfYear(date: Date): Date {
        return new Date(date.getFullYear(), 11, 31, 0, 0, 0);
    }

    static getDateFromMomentOrDate(date: any): Date {
        if (date instanceof moment) {
            return moment(date).toDate();
        }

        // Could also be a string
        return new Date(date);
    }

    static getTimeZone(): string {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    static getStartOfDay(date: Date): Date {
        return new Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    static getDateOrDefault(value: Date | string | null): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof moment) {
            return moment(value).toDate();
        }

        return new Date(value);
    }

    public static isDateString(value) {
        if (value === null || value === undefined || typeof value !== 'string') {
            return false;
        }

        const regex = /^\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(?:\.\d+)?Z?/;

        return regex.test(value);
    }

    public static getYearStartDate(year: number): Date {
        const startDate = new Date(year, 0, 1);
        return startDate;
    }

    public static getYearEndDate(year: number): Date {
        const endDate = new Date(year, 12, 0);
        return endDate;
    }

    public static getCurrentYear(): number {
        const currentDate = new Date();
        return currentDate.getFullYear();
    }

    public static getYearRange(yearRange: number): number[] {
        const currentYear = new Date().getFullYear();
        const startYear = currentYear - yearRange;
        const endYear = currentYear + yearRange;
        const years: number[] = [];

        for (let i = startYear; i <= endYear; i++) {
            years.push(i);
        }

        return years;
    }

    public static addHoursAndMinutes(date: Date | string, hours: number, minutes: number) {
        let newDate = new Date(date);
        newDate.setHours(hours);
        newDate.setMinutes(minutes);
        newDate.setSeconds(0);

        return newDate;
    }

    public static addHoursAndMinutesFromTimeString(date: Date | string, time: string) {
        let hours: number = parseInt(time.substring(0, 2));
        let minutes: number = parseInt(time.substring(3));

        return this.addHoursAndMinutes(date, hours, minutes);
    }

    public static getYearWeekNumber(date: Date) {
        return currentWeekNumber(date);
    }

    public static getTimeStringFromDate(date: Date): string {
        let newDate = new Date(date);
        let hours = newDate.getHours().toString();
        let minutes = newDate.getMinutes().toString();

        hours = this.padLeadingZero(hours);
        minutes = this.padLeadingZero(minutes);

        return `${hours}:${minutes}`;
    }

    public static getTimeStamp(date: Date): string {
        return date.getFullYear() + '' + (date.getMonth() + 1) + '' + date.getDate() + '_' + date.getHours() + '' + date.getMinutes();
    }

    public static getMonthDifference(startDate: Date, endDate: Date): Number {
        startDate = new Date(startDate);
        endDate = new Date(endDate);
        let months = (endDate.getFullYear() - startDate.getFullYear()) * 12;
        months -= startDate.getMonth();
        months += endDate.getMonth();
        return months;
    }

    private static padLeadingZero(value: string) {
        if (value.length === 1) {
            value = `0${value}`;
        }

        return value;
    }

    public static getStartOfYear(year: number): Date {
        return new Date(year, 0, 1);
    }

    public static getEndOfYear(year: number): Date {
        return new Date(year, 11, 31);
    }

    public static convertDate(date: Date): Date {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    }

    public static convertDateTime(date: Date): Date {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
    }

    public static convertDateToUTCDate(date: Date): Date {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    }

    public static convertDateTimeToUTCDateTime(date: Date): Date {
        return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
    }

    public static getUTCDate(date: Date): Date {
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), 0, 0, 0, 0);
    }

    public static getUTCDateTime(date: Date): Date {
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), 0);
    }

    public static daysInMonth(month, year) {
        return new Date(year, month, 0).getDate();
    }

    public static getToday(): Moment {
        return this.getStartOfDayDate(moment());
    }

    public static formatFileDate(date?: Date): string {
        if (!date) {
            date = this.getStartOfDayDate(moment()).toDate();
        } else {
            date = new Date(date);
        }
        return date.getFullYear().toString() + ('0' + (date.getMonth() + 1)).slice(-2) + ('0' + date.getDate()).slice(-2);
    }

    public static addDays(date: Date, days: number): Date {
        const addDate = new Date(date);
        addDate.setDate(addDate.getDate() + days);
        return addDate;
    }

    public static getStartOfDayDate(date: Moment): Moment {
        return date.startOf('day');
    }

    public static getStartOfMonthDate(date: Moment): Moment {
        return this.getStartOfDayDate(date)
            .startOf('month');
    }

    public static getEndOfMonthDate(date: Moment): Moment {
        return date.endOf('day')
            .endOf('month');
    }

    public static getStartOfCurrentMonthDate(): Moment {
        const date = moment(new Date());
        return this.getStartOfDayDate(date)
            .startOf('month');
    }

    public static getEndOfCurrentMonthDate(): Moment {
        const date = moment(new Date());
        return date.endOf('day')
            .endOf('month');
    }

    public static getStartOfWeekDate(): Date {
        const today = new Date((new Date).toDateString());
        const startDay = today.getDate() - today.getDay() + 1;
        return new Date(today.setDate(startDay))
    }

    public static getEndOfWeekDate(): Date {
        const today = new Date((new Date).toDateString());
        const startDay = today.getDate() - today.getDay();
        const lastDay = startDay + 7;
        return new Date(today.setDate(lastDay))
    }

    public static timeSpanConvert(n: number): string {
        var num = n;
        var hours = (num / 60);
        var rhours = Math.floor(hours);
        var minutes = (hours - rhours) * 60;
        var rminutes = Math.round(minutes);

        var h = String(rhours)
        if (h.length == 1) {
            h = '0' + h
        }

        var m = String(rminutes)
        if (m.length == 1) {
            m = '0' + m
        }
        return h + ':' + m;
    }

    public static getDifferenceTime(date: Date): string {
        var now = this.convertDateTime(new Date());
        now.setSeconds(0);
        date = new Date(date);
        date.setSeconds(0);
        var difference = now.getTime() - date.getTime();
        return this.timeSpanConvert(Math.floor(difference / 60000));
    }

    public static getDifferenceBetweenTimes(firstDate: Date, secondDate: Date): number {
        var difference = secondDate.getTime() - firstDate.getTime();
        return difference / 60000 / 60;
    }

    private static readonly iso8601 = /^\d{4}-\d\d-\d\dT\d\d:\d\d:\d\d(\.\d+)?(([+-]\d\d:\d\d)|Z)?$/;

    public static isIso8601(value) {
        if (value === null || value === undefined || typeof value !== 'string') {
            return false;
        }

        return this.iso8601.test(value);
    }

    public static addOffset(date: Date): Date {
        date = new Date(date);
        date.setMinutes(date.getMinutes() + (date.getTimezoneOffset() * -1));
        return date;
    }

    public static removeOffset(date: Date): Date {
        date = new Date(date);
        date.setMinutes(date.getMinutes() + date.getTimezoneOffset());
        return date;
    }

    public static isStartAndEndTimeLastSecondOfTheDay(startTime: Date, endTime: Date): boolean {
        return startTime.getUTCHours() == 23 && endTime.getUTCHours() == 23 && startTime.getUTCMinutes() == 59 && endTime.getUTCMinutes() == 59 && startTime.getUTCSeconds() == 59 && endTime.getUTCSeconds() == 59;
    }

    public static isStartTimeLastSecondOfTheDay(startTime: Date): boolean {
        return startTime.getUTCHours() == 23 && startTime.getUTCMinutes() == 59 && startTime.getUTCSeconds() == 59;
    }

    public static getHourMinuteFormat(date: Date): string {
        if (date.toString().includes(' ')) {
            const fulltime = date.toTimeString().split(' ')[0].split(':');
            return fulltime[0] + ':' + fulltime[1];
        }
        else {
            const fulltime = date.toString().split('T')[1].split(':');
            return fulltime[0] + ':' + fulltime[1];
        }
    }

    public static getNextQuarterMonth(month: number): number {
        return (Math.floor((month - 1) / 3) + 1) * 3;
    }

    public static getNextQuarterMonthFromZero(monthIndex: number): number {
        return this.getNextQuarterMonth(monthIndex + 1) - 1;
    }

    public static setNextQuarterMonth(date: Date): void {
        const nextMonth = this.getNextQuarterMonthFromZero(date.getMonth());

        date.setMonth(nextMonth);
    }

    public static getNextHalfYearMonth(month: number): number {
        return (Math.floor((month - 1) / 6) + 1) * 6;
    }

    public static getNextHalfYearMonthFromZero(monthIndex: number): number {
        return this.getNextHalfYearMonth(monthIndex + 1) - 1;
    }

    public static setNextHalfYearMonth(date: Date): void {
        const nextMonth = this.getNextHalfYearMonthFromZero(date.getMonth());

        date.setMonth(nextMonth);
    }

    public static dateRangesIntersect(startDate1: Date, endDate1: Date, startDate2: Date, endDate2: Date): boolean {
        return (startDate1 <= endDate2) && (endDate1 >= startDate2);
    }

    public static get maxDate(): Date {
        return new Date(8640000000000000);
    }

    public static toDate(value: Date | Moment | string): Date | null {
        if (!value) {
            return null;
        }

        if (value instanceof Date) {
            return value;
        }
        else if (value instanceof moment) {
            return (value as Moment).toDate();
        }

        return new Date(value as string);
    }

    public static getDayNameByStringDate(date: string) {
        return new Date(date).toLocaleString('en-us', { weekday: 'long' })
    }

    public static getDayName(date: Date) {
        const day = new Date(date);
        return this.dayNames[day.getDay()];
    }

    static readonly days = ['so', 'mo', 'di', 'mi', 'do', 'fr', 'sa'];

    public static getDayOfWeek(date: string) {
        return this.days[new Date(date).getDay()];
    }

    public static isDatesGreaterThanOneyear(firstDate: Date, secondDate: Date) {
        const yearInMilliSeconds = 365 * 24 * 60 * 60 * 1000;  //365 * 24 * 60 * 60 * 1000  => gives 1 year 
        return (Math.abs(secondDate.getTime() - firstDate.getTime()) > yearInMilliSeconds)
    }

    public static getQuarterMonths(currentMonth): string {
        const startMonth = Math.floor(currentMonth / 3) * 3;
        const endMonth = startMonth + 2;
        return startMonth + ' ' + endMonth;
    }

    public static getTimeNumberFormat(number): any {
        if (number <= 9) {
            number = '0' + number;
        }
        return number;
    }

    public static getDayFromDate(date: number | undefined, month: number, year: number): string {
        return this.dayNames[new Date(((month) + '/' + date + '/' + year)).getDay()];
    }

    public static getTimeSpanFromMinutes(n: number): string {
        const hours = Math.floor(n / 60);
        const minutes = Math.round(n % 60);

        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    }

    public static getFormatedHoursMinutes(hours: string): string {
        hours = hours.replace('.', '').replace(':', '');
        if (hours.length > DurationCharacterLength.FiveCharacters) {
            return '';
        }

        if (hours.length == DurationCharacterLength.SingleCharacter)
            return '0' + hours + ':00';

        if (hours.length == DurationCharacterLength.TwoCharacters)
            return hours + ':00';

        if (hours.length == DurationCharacterLength.ThreeCharacters) {
            var first = parseInt(hours.substring(0, 1));
            var second = parseInt(hours.substring(1, 2));
            if ((first > 0 && first <= 9) && (second >= 0 && second <= 5)) {
                return '0' + first + ':' + second + hours.substring(2, 3);
            }
            else {
                return hours.substring(0, 2) + ':' + hours.substring(2, 3) + '0';
            }
        }

        if (hours.length == DurationCharacterLength.FourCharacters) {
            return hours.substring(0, 2) + ':' + hours.substring(2, 4);
        }

        if (hours.length == DurationCharacterLength.FiveCharacters) {
            return hours.substring(0, 3) + ':' + hours.substring(3, 5);
        }
        return '';
    }

    public static dateDifference(startDate: Date, endDate: Date): { years: number, months: number } {
        // Convert the dates to Date objects
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (start > end) {
            return { years: 0, months: 0 };
        }

        let years = end.getFullYear() - start.getFullYear();
        let months = end.getMonth() - start.getMonth();

        // Adjust if the month difference is negative
        if (months < 0) {
            years--;
            months += 12;
        }

        // Adjust if the day difference is negative
        if (end.getDate() < start.getDate()) {
            months--;
            if (months < 0) {
                years--;
                months += 12;
            }
        }

        return { years, months };
    }

    public static calculateAge(birthDate: Date | string | null | undefined, translate: TranslateService): string {
        if (birthDate) {
            const date = new Date(birthDate);

            if (isNaN(date.getTime())) {
                return '';
            }

            const now = new Date();
            const age = DateTimeHelper.dateDifference(date, now);

            const diffInMs = now.getTime() - date.getTime();
            const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
            const diffInWeeks = Math.floor(diffInDays / 7);

            // Case 1: Age 0–4 months → show in weeks only
            if (age.years === 0 && age.months < 4) {
                return `${diffInWeeks} ${diffInWeeks > 1 ? translate.instant('Common.WeeksShort') : translate.instant('Common.WeekShort')}`;
            }

            // Case 2: 4 months – 11 months → show in months and weeks
            if (age.years === 0 && age.months >= 4) {
                const remainingDays = diffInDays - (age.months * 30);
                const remainingWeeks = Math.floor(remainingDays / 7);

                return `${age.months} ${age.months > 1 ? translate.instant('Common.MonthsShort') : translate.instant('Common.MonthShort')} ${remainingWeeks} ${remainingWeeks > 1 ? translate.instant('Common.WeeksShort') : translate.instant('Common.WeekShort')}`;
            }

            // Case 3: 1 year or more → show in years and months
            if (age.years > 0 && age.months > 0) {
                return `${age.years} ${age.years > 1 ? translate.instant('Common.YearsShort') : translate.instant('Common.YearShort')} ${age.months} ${age.months > 1 ? translate.instant('Common.MonthsShort') : translate.instant('Common.MonthShort')}`;
            } else if (age.years > 0) {
                return `${age.years} ${age.years > 1 ? translate.instant('Common.YearsShort') : translate.instant('Common.YearShort')}`;
            }
        }
        return '';
    }

    public static convertDecimalToTime(decimal: number | null | undefined): string {
        if (decimal == null || isNaN(decimal)) return '00:00';

        const sign = Math.sign(decimal);
        const absoluteValue = Math.abs(decimal);

        const hours = Math.floor(absoluteValue);
        const minutes = Math.round((absoluteValue - hours) * 60);

        const hourPart = hours.toString().padStart(2, '0');
        const minutePart = minutes.toString().padStart(2, '0');

        return `${sign === -1 ? '-' : ''}${hourPart}:${minutePart}`;
    }
}
